import type { AppNotification } from '../models/index';

/** Criteria to clear integrity rows from the in-app inbox after disposition. */
export type IntegrityInboxMatch = {
  runId?: string;
  signalCode?: string;
  houseId?: string;
};

function metaString(meta: Record<string, unknown> | undefined, key: string): string {
  const value = meta?.[key];
  return typeof value === 'string' ? value.trim() : '';
}

function metaSignalCodes(meta: Record<string, unknown> | undefined): string[] {
  if (!meta) return [];
  if (Array.isArray(meta.signalCodes)) {
    return meta.signalCodes.map((c) => String(c).trim()).filter(Boolean);
  }
  const single = metaString(meta, 'signalCode');
  return single ? [single] : [];
}

/**
 * Whether an inbox notification belongs to an integrity signal being closed
 * (suppressed / false-positive).
 */
export function matchesIntegrityNotification(
  notif: Pick<AppNotification, 'cat' | 'title' | 'body' | 'meta'>,
  match: IntegrityInboxMatch,
): boolean {
  if (String(notif.cat ?? '') !== 'integrity') return false;

  const meta = notif.meta;
  const metaRunId = metaString(meta, 'runId');
  const metaHouseId = metaString(meta, 'houseId');
  const codes = metaSignalCodes(meta);

  const wantCode = String(match.signalCode ?? '').trim();
  const wantRun = String(match.runId ?? '').trim();
  const wantHouse = String(match.houseId ?? '').trim();

  if (wantHouse && metaHouseId && metaHouseId !== wantHouse) return false;

  // When a runId is specified, never clear rows from a different run.
  if (wantRun) {
    if (metaRunId && metaRunId === wantRun) {
      if (!wantCode) return true;
      if (codes.length === 0) return true;
      return codes.includes(wantCode);
    }
    // Legacy inbox rows without runId: best-effort by code / copy only.
    if (!metaRunId && wantCode) {
      if (codes.includes(wantCode)) return true;
      if (codes.length === 0) {
        const hay = `${notif.title ?? ''} ${notif.body ?? ''}`.toLowerCase();
        if (hay.includes(wantCode.toLowerCase())) return true;
        if (wantCode === 'config_tamper_burst') return /config|tamper|sabot/i.test(hay);
      }
    }
    return false;
  }

  if (wantCode && codes.includes(wantCode)) return true;

  // Older FCM / incomplete meta: best-effort match on visible copy.
  if (wantCode && codes.length === 0 && !metaRunId) {
    const hay = `${notif.title ?? ''} ${notif.body ?? ''}`.toLowerCase();
    if (hay.includes(wantCode.toLowerCase())) return true;
    if (wantCode === 'config_tamper_burst') {
      return /config|tamper|sabot/i.test(hay);
    }
  }

  return false;
}

export function filterOutMatchingIntegrity(
  items: AppNotification[],
  match: IntegrityInboxMatch,
): AppNotification[] {
  return items.filter((n) => !matchesIntegrityNotification(n, match));
}
